import { Component, Input, Output, EventEmitter, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { dateRangeValidator } from '../../../../shared/validators/date-validators';
import { CampaignMilestone, LabeledValue, TimelineDisplayMode } from '../../../../shared/interfaces';
import { NgbDateStruct, NgbModalRef, NgbDateParserFormatter, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from '../../../../shared/services/datepicker-adapter';
import { SimulatorCriteriaForm2CascadeService } from '../services/simulator-criteria-form2-cascade.service';

@Component({
  selector: 'timeline-simulator-criteria-form2',
  templateUrl: './timeline-simulator-criteria-form2.component.html',
  styleUrl: './timeline-simulator-criteria-form2.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    SimulatorCriteriaForm2CascadeService,
  ],
  standalone: false,
})
export class TimelineSimulatorCriteriaForm2Component implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cascadeService = inject(SimulatorCriteriaForm2CascadeService);
  private _simulatorForm: FormGroup;
  private readonly customDateParserFormatter = inject(CustomDateParserFormatter);

  @Input()
  get simulatorForm(): FormGroup {
    return this._simulatorForm;
  }
  set simulatorForm(value: FormGroup) {
    this._simulatorForm = value;
    this.simulatorFormChange.emit(value);
  }
  @Output() simulatorFormChange = new EventEmitter<FormGroup>();
  @Output() simulate = new EventEmitter<void>();

  expectedMad: NgbDateStruct | null = null;
  isInvalidMad: boolean = false;
  minDate: NgbDateStruct = { year: 2010, month: 1, day: 1 };
  maxDate: NgbDateStruct = { year: 2050, month: 12, day: 31 };
  availableProductLaunchTypes: LabeledValue<string>[] = [];
  availableComplexities: LabeledValue<string>[] = [];
  availableCampaignTypes: LabeledValue<string>[] = [];
  availablePosm: string[] = [];
  availableHallmarksTypes: LabeledValue<string>[] = [];
  availableCollectors: string[] = [];
  availableCollectorComplexities: LabeledValue<string>[] = [];
  availableSets: string[] = [];
  timeline: CampaignMilestone[] = [];
  isLoading: boolean = false;
  modal: NgbModalRef;
  readonly minimumDateLength = 10;

  get TimelineCampaignTypes() {
    return this.cascadeService.TimelineCampaignTypes;
  }
  get TimelineProductLaunchTypes() {
    return this.cascadeService.TimelineProductLaunchTypes;
  }
  get CampaignTypes() {
    return this.cascadeService.CampaignTypes;
  }
  get ComplexityLevels() {
    return this.cascadeService.ComplexityLevels;
  }
  get PosmValues() {
    return this.cascadeService.PosmValues;
  }
  get HallmarksTypes() {
    return this.cascadeService.HallmarksTypes;
  }
  get HallmarksTypeTexts() {
    return this.cascadeService.HallmarksTypeTexts;
  }
  get CollectorTypes() {
    return this.cascadeService.CollectorTypes;
  }

  ngOnInit() {
    this.availableProductLaunchTypes = this.cascadeService.getAvailableProductLaunchTypes();
    this.availableHallmarksTypes = this.cascadeService.getAvailableHallmarksTypes();

    this.simulatorForm = this.fb.group({
      projectType: [this.TimelineCampaignTypes.PRODUCT_CAMPAIGN, [Validators.required]],
      expectedMad: ['', [dateRangeValidator(2010, 2050)]],

      productLaunchType: [this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS],
      complexity: [this.ComplexityLevels.ONE_TWO_T],
      campaignType: [this.CampaignTypes.STANDARD],
      posm: [this.PosmValues.DMI_PRODUCED],

      hallmarksType: [this.HallmarksTypes.GLOBAL_HALLMARKS],
      collectorType: [this.CollectorTypes.INCLUDE_FOR_CHINA],
      collectorComplexity: [this.ComplexityLevels.ONE_TWO_T],
      set: [this.PosmValues.DMI_PRODUCED],
    });

    this.switchProductLaunchType(this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS);
  }

  switchHallmarksType(value: string) {
    this.cascadeService.handleHallmarksTypeSwitch(this.simulatorForm, value);

    this.availableCollectors = this.cascadeService.getAvailableCollectors(value);
    this.availablePosm = this.cascadeService.getAvailablePosmForHallmarks(value);
    this.availableSets = this.cascadeService.getAvailableSets(value);

    const firstCollector = this.availableCollectors.length ? this.availableCollectors[0] : '';
    if (firstCollector) {
      this.availableCollectorComplexities = this.cascadeService.getAvailableCollectorComplexities(firstCollector);
    } else {
      this.availableCollectorComplexities = [];
    }
  }

  switchProjectType(value: string) {
    this.cascadeService.handleProjectTypeSwitch(this.simulatorForm, value);

    if (value === this.TimelineCampaignTypes.PRODUCT_CAMPAIGN) {
      this.switchProductLaunchType(this.TimelineProductLaunchTypes.CHINA_NORMAL_COSMETICS);
    } else {
      this.switchHallmarksType(this.HallmarksTypes.GLOBAL_HALLMARKS);
    }
  }

  switchProductLaunchType(value: string) {
    this.cascadeService.handleProductLaunchTypeSwitch(this.simulatorForm, value);
    this.availableComplexities = this.cascadeService.getAvailableComplexities(value);
    this.availableCampaignTypes = this.cascadeService.getAvailableCampaignTypes(value);

    const firstCampaignType = this.availableCampaignTypes.length ? this.availableCampaignTypes[0].value : '';
    if (firstCampaignType) {
      this.availablePosm = this.cascadeService.getAvailablePosm(firstCampaignType);
    } else {
      this.availablePosm = [];
    }
  }

  onMonthNavigate($event) {
    if (!$event.current || $event.next.month == $event.current.month) return;
    this.expectedMad = { year: $event.next.year, month: $event.next.month, day: 1 };
  }

  containsAnyLetter(input: string): boolean {
    const letterRegex = /[a-zA-Z]/;
    return letterRegex.test(input);
  }

  toDateObject(dateObj: { day: number; month: number; year: number }): Date {
    const { day, month, year } = dateObj;
    return new Date(year, month - 1, day);
  }

  isDateInRange(
    inputDate: { day: number; month: number; year: number },
    minDate: { day: number; month: number; year: number },
    maxDate: { day: number; month: number; year: number }
  ): boolean {
    const input = this.toDateObject(inputDate);
    const min = this.toDateObject(minDate);
    const max = this.toDateObject(maxDate);

    return input.getTime() >= min.getTime() && input.getTime() <= max.getTime();
  }

  /**
   * Handles the selection of expectedMad field.
   * @param $event - The event object from the date picker or input field
   * @param inputOrSelect - source: 'input' for manual input, 'select' for date picker selection.
   *
   * @description
   * When inputOrSelect is 'input':
   * - Validates that the input contains no letters (only numbers and special characters allowed)
   * - Ensures the input is at least 10 characters long for proper date format
   * - Uses CustomDateParserFormatter to validate and parse the date string
   * - Checks if the parsed date falls within the allowed range (2010-2050)
   *
   * When inputOrSelect is 'select' (date picker selection):
   * - Directly uses the provided date object without additional validation
   */
  onExpectedMadSelect($event: Input | NgbDateStruct, inputOrSelect: 'input' | 'select') {
    let dateResult: NgbDateStruct;
    if (inputOrSelect == 'input') {
      const htmlEl = ($event as Event).target as HTMLInputElement;
      const inputValue = htmlEl.value;

      if (inputOrSelect == 'input' && this.containsAnyLetter(inputValue)) {
        this.isInvalidMad = true;
        return;
      }

      if (inputValue.length < this.minimumDateLength) {
        this.isInvalidMad = false;
        return;
      }

      if (inputValue.length != 0 && !this.customDateParserFormatter.validateDate(inputValue)) {
        this.isInvalidMad = true;
        return;
      }

      if (inputValue.length != 0) {
        dateResult = this.customDateParserFormatter.parse(inputValue);
      } else {
        this.isInvalidMad = true;
        return;
      }

      if (!this.isDateInRange(dateResult, this.minDate, this.maxDate)) {
        this.isInvalidMad = true;
        return;
      }
    } else {
      dateResult = $event as NgbDateStruct;
    }
    this.isInvalidMad = false;
    this.expectedMad = dateResult;
  }

  switchComplexity(value: string) {
    this.simulatorForm.patchValue({
      complexity: value,
    });
  }

  switchCampaignType(value: string) {
    this.cascadeService.handleCampaignTypeSwitch(this.simulatorForm, value);
    this.availablePosm = this.cascadeService.getAvailablePosm(value);
  }

  switchCollectorType(value: string) {
    this.cascadeService.handleCollectorTypeSwitch(this.simulatorForm, value);
    this.availableCollectorComplexities = this.cascadeService.getAvailableCollectorComplexities(value);
  }

  switchSet(value: string) {
    this.simulatorForm.patchValue({
      set: value,
    });
  }

  switchCollectorComplexity(value: string) {
    this.simulatorForm.patchValue({
      collectorComplexity: value,
    });
  }

  switchPosm(value: string) {
    this.simulatorForm.patchValue({
      posm: value,
    });
  }
}
